import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { mysqlAdditionalFieldsKeyOptions, SSLMode, sslModeOptions } from 'app/registries/consts/mysql-consts';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind, Type } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

const connectionStringsControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child:
        {
            type: 'textbox',
            bind: 'server',
            label: stringResources.ModelName_ServerName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child:
        {
            type: 'textbox',
            bind: 'port',
            label: stringResources.ModelName_Port,
            isLabelVertical: true,
            inputType: 'number',
            min: 0,
            defaultValue: 3306
        }
    },
    {
        type: 'parameterizable',
        child:
        {
            type: 'textbox',
            bind: 'database',
            label: stringResources.ModelName_DatabaseName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        }
    },
    // TODO: Align with copy wizard, we don't support schema in linked service.
    // {
    //     type: 'textbox',
    //     bind: 'schema',
    //     label: stringResources.Schema,
    //     isLabelVertical: true
    // },
    {
        type: 'parameterizable',
        child:
        {
            type: 'textbox',
            bind: 'username',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        }
    },
    {

        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            child:
            {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            },
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.MySql_DisplayText),
                stringResources.ModelName_Password)
        }
    },
    {
        type: 'select-switch',
        bind: 'sslMode',
        label: stringResources.SSL_Mode,
        isLabelVertical: true,
        options: sslModeOptions,
        defaultValue: SSLMode.Preferred,
        cases: [
            {
                caseValue: SSLMode.Disabled,
                children: []
            },
            {
                caseValue: null,
                children: [
                    {
                        type: 'radio',
                        bind: 'useSystemTrustStore',
                        isLabelVertical: true,
                        options: [
                            {
                                label: stringResources.ModelName_UseSystemTrustStore,
                                value: true
                            },
                            {
                                label: stringResources.Mysql_Not_Use_System_Trust_Store,
                                value: false
                            }
                        ],
                        defaultValue: false
                    }
                ]
            }
        ]
    }
];

const conectionStrsAkvControl: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'azure-key-vault',
                    enableAKVParameterization: true,
                    bind: 'referedAKV',
                    isLabelVertical: true,
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.MySql_DisplayText),
                        'Server=<server>;Port=<port>;Database=<database>;User=<username>;Password=<password>')
                }
            ]
        },
        {
            caseValue: false,
            children: [
                ...connectionStringsControls,
                {
                    type: 'key-val-list',
                    bind: 'additionalFields',
                    label: stringResources.AdditionalConnectionStringFields
                }
            ]
        }]
};

export const mysqlMetadata: IDynamicLinkedServiceTypeMetadata = async () => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.mysql.linkedServiceType,
        displayName: stringResources.MySql_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-MySQL'
        },
        supportDatasetV2: true,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.MySql
        },
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.Basic
        ],
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: __IS_FABRIC_DPE__,
        supportMultipleIncrementalColumns: true,
        isFirstClass: true,
        fetchTableNameFromItemPath: true,
        tableColumnNameQuotes: ['`', '`'],
        category: ['Database'],
        copySinkTypes: __IS_FABRIC_DPE__ ? [adfModelTypeMap.mysql.copySinkType] : [],
        inPreview: 'none',
        copySourceTypes: [adfModelTypeMap.mysql.copySourceType],
        supportFirstClassParameterExperience: true,
        isDeprecated: !GlobalVariables.showLegacy ? '{ isLegacyConnector === true }' : false,
        showWarningMessage: '{ isLegacyConnector === true}',
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
            stringResources.DriverVersion.toLocaleLowerCase()
        ),
        warningMessageHyperlink:
            'https://learn.microsoft.com/azure/data-factory/connector-mysql',
        controls: [
            {
                type: 'radio-switch',
                bind: 'driverVersion',
                label: stringResources.DriverVersion,
                isLabelVertical: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                hide: !GlobalVariables.showLegacy ? '{driverVersion === "v2" }' : false,
                cases: [
                    {
                        caseValue: driverVersions.v2,
                        children: connectionStringsControls
                    },
                    {
                        caseValue: driverVersions.v1,
                        children: [conectionStrsAkvControl]
                    }
                ]
            },
            ...(connectionStringsControls.map((control) => ({
                ...control,
                hide: !GlobalVariables.showLegacy ? '{driverVersion !== "v2" }' : true
            }))),
            {
                type: 'parameter-config',
                hide: '{driverVersion !== "v2" }',
                bind: 'parameterizableAdditionalFields',
                label: stringResources.AdditionalConnectionStringFields,
                isLayoutVertical: true,
                isKeyDropdown: true,
                keyOptions: mysqlAdditionalFieldsKeyOptions,
                keyOptionHasType: true,
                dynamicInputTypes: true,
                dynamicInputTypesParameterizable: true,
                keyLabel: stringResources.PropertyName,
                defaultWidth: 250
            }
        ],
        additionalPropertiesInFabric: GlobalVariables.connectionPropertiesForMySql ? [
            {
                label: 'Allow zero datetime',
                value: 'allowZeroDatetime',
                type: Type.Boolean
            },
            {
                label: 'Connection timeout',
                value: 'connectionTimeout',
                type: Type.Int
            },
            {
                label: 'Convert zero datetime',
                value: 'convertZeroDatetime',
                type: Type.Boolean
            },
            {
                label: 'GUID format',
                value: 'guidFormat',
                type: Type.String
            },
            {
                label: 'Treat tiny as boolean',
                value: 'treatTinyAsBoolean',
                type: Type.Boolean
            }
        ] : undefined
    };
    return metadata;
};
